import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Filter, Displayable, TierCosmetic } from '../../models/filter-models';
import { DisplayType } from "../../models/enums";
import { FormsModule } from '@angular/forms';
import { ItemDataService } from '../../services/item-data.service';
import { UniqueItem, ChanceBaseTierType, TieredChanceBase, FlatItemData } from '../../models/item-data-models';
import { ItemLabelComponent } from '../../common/item-label/item-label.component';
import { FilterService } from '../../services/filter.service';
import { TierStyleComponent } from '../../common/tier-style/tier-style.component';
import { ChanceBasesTierService } from '../../services/tiering/chance-bases-tier.service';
import { UniquesTierService } from '../../services/tiering/uniques-tier.service';
import { AutocompleteComponent } from '../../common/autocomplete/autocomplete.component';
import { BaseComponent } from '../../common/base.component';
import { GlobalShortcutFocusDirective } from '../../common/global-shortcut-focus.directive';
import { DynamicContentComponent } from '../../common/dynamic-content/dynamic-content.component';
import { hasMovedFromOriginalTier } from '../tier-helpers';

interface TieredChanceBaseVm extends TieredChanceBase {
  baseTypeImage: string;
  uniquesWithValuesAndImages: { uniqueName: string, image: string, value?: number }[];
}

interface TierDataVm {
  tier: ChanceBaseTierType;
  name: string;
  styleDialogTitle: string;
  price: string;
  cosmetics: Displayable;
  exampleName: string;
  show: boolean;
  classes: { class: string, items: TieredChanceBaseVm[] }[];
  isDragOver?: boolean;
  dragCounter?: number;
}

@Component({
  selector: 'app-chance-base-tiers',
  templateUrl: './chance-base-tiers.component.html',
  styleUrls: ['./chance-base-tiers.component.scss', '../tier-tab-common.scss'],
  imports: [FormsModule, ItemLabelComponent, TierStyleComponent, AutocompleteComponent, GlobalShortcutFocusDirective, DynamicContentComponent]
})
export class ChanceBaseTiersComponent extends BaseComponent implements OnChanges {
  @Input({ required: true }) filter!: Filter;
  @Input() changedOnly = false;

  itemData: FlatItemData[];
  uniquesData: UniqueItem[];
  tierCutoffValues: { tier: ChanceBaseTierType, value: number, displayValue?: string }[];
  tieredChanceBases: Record<ChanceBaseTierType, TieredChanceBase[]>;
  classes: { name: string, image: string }[] = [];
  hardCodedChanceBases: string;

  tierList: TierDataVm[] = [];
  filteredOtherBaseTypes: { class: string, items: TieredChanceBaseVm[] }[] = [];

  draggedItem: TieredChanceBaseVm | null = null;

  uniqueFilter: string = '';

  DisplayType = DisplayType;

  constructor(private itemDataService: ItemDataService, private filterService: FilterService,
    private chanceBasesTierService: ChanceBasesTierService,
    private uniquesTierService: UniquesTierService) {
    super();
    this.itemData = this.itemDataService.getFlatItemData();
    this.uniquesData = this.itemDataService.getUniquesList();
    this.tierCutoffValues = this.chanceBasesTierService.getTierCutoffValues();
    this.tieredChanceBases = this.chanceBasesTierService.getTieredChanceBases();
    this.classes = this.itemDataService.getClasses();
    this.hardCodedChanceBases = this.itemDataService.getHardCodedChanceBases().join(', ');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter'] || changes['changedOnly']) {
      this.buildTierList();
    }
  }

  buildTierList(): void {
    this.tierList = Object.entries(this.tieredChanceBases)
      .map(([tier, items]) => {
        const cosmetics = tier === 'Other' ? new TierCosmetic() : this.filter.chanceBaseTierCosmetics.find(c => c.chanceBaseTier === tier || (tier === 'Class' && c.chanceBaseTier === 'Potential'));

        return {
          tier,
          name: tier === 'Class' ? 'Potential Class-specific' : tier === 'Searching' ? 'Favourite' : tier,
          styleDialogTitle: (tier === 'Class' ? 'Potential' : tier === 'Searching' ? 'Favourite' : tier) + ' Chance Bases',
          price: tier === 'Class'
            ? this.tierCutoffValues.find(t => t.tier === 'Other')?.displayValue?.toString()
            : this.tierCutoffValues.find(t => t.tier === tier)?.displayValue?.toString(),
          cosmetics: cosmetics,
          exampleName: items[0]?.baseType,
          show: cosmetics === undefined ? true : cosmetics.displayOptions.hidden,
          classes: items.reduce((acc, item) => {
            const className = item.classSpecific || 'Default';
            let itemsByClassName = acc.find(i => i.class === className);
            if (!itemsByClassName) {
              acc.push({ class: className, items: [] });
              itemsByClassName = acc.find(i => i.class === className);
            }

            const uniquesWithValuesAndImages: { uniqueName: string, image: string | undefined, value?: number }[] = [];

            item.uniques.forEach(uniqueOfBaseType => {
              const image = uniqueOfBaseType.image ? '/img/uniques/' + uniqueOfBaseType.image : undefined;
              uniquesWithValuesAndImages.push({ uniqueName: uniqueOfBaseType.name, image, value: uniqueOfBaseType.price });
            });

            const flatItemData = this.itemDataService.getFlatItemDataByBaseTypeName(item.baseType);

            const itemVm = { ...item, uniquesWithValuesAndImages, baseTypeImage: flatItemData?.baseType.image } as TieredChanceBaseVm;

            if (this.uniqueFilter && this.uniqueFilter.length > 1 && (
              !item.uniques.some(uniqueOfBaseType => uniqueOfBaseType.name.toLowerCase().includes(this.uniqueFilter.toLowerCase()))
              && !item.baseType.toLowerCase().includes(this.uniqueFilter.toLowerCase()))) {
              return acc;
            }

            itemsByClassName!.items.push(itemVm);
            return acc;
          }, [] as { class: string, items: TieredChanceBaseVm[] }[])
        } as TierDataVm;
      }).filter(t => !!t)
      .map(t => this.applyChangedOnly(t));


      Object.values(this.tierList).forEach(tier => {
        tier.classes.forEach(group => {
          group.items.sort((a, b) => b.uniquesWithValuesAndImages.reduce((acc, curr) => acc + (curr.value || 0), 0) - a.uniquesWithValuesAndImages.reduce((acc, curr) => acc + (curr.value || 0), 0));
        });
      });

    this.updateFilteredOtherBaseTypes();
  }

  private applyChangedOnly(t: TierDataVm): TierDataVm {
    if (!this.changedOnly) return t;
    const tierKey = t.tier as ChanceBaseTierType;
    return {
      ...t,
      classes: t.classes
        .map(g => ({
          ...g,
          items: g.items.filter(item => hasMovedFromOriginalTier(item.originalTier, tierKey)),
        }))
        .filter(g => g.items.length > 0),
    };
  }

  updateFilteredOtherBaseTypes() {
    if (!this.uniqueFilter || this.uniqueFilter.length < 2) {
      this.filteredOtherBaseTypes = this.tierList.find(t => t.tier === 'Other')!.classes
      .map(classGroup => ({
        ...classGroup,
        items: classGroup.items.filter(item => hasMovedFromOriginalTier(item.originalTier, 'Other'))
      }))
      .filter(classGroup => classGroup.items.length > 0)
      return;
    }

    this.filteredOtherBaseTypes = this.tierList.find(t => t.tier === 'Other')!.classes
      .map(classGroup => ({
        ...classGroup,
        items: classGroup.items.filter(item =>
          item.uniquesWithValuesAndImages.some(unique => unique.uniqueName.toLowerCase().includes(this.uniqueFilter.toLowerCase()))
          || item.baseType.toLowerCase().includes(this.uniqueFilter.toLowerCase())
          || hasMovedFromOriginalTier(item.originalTier, 'Other')
        )
      }))
      .filter(classGroup => classGroup.items.length > 0)
  }

  updateFilter() {
    this.filterService.updateFilter();
  }

  onDragStart(event: DragEvent, baseType: TieredChanceBaseVm) {
    this.draggedItem = baseType;
    event.dataTransfer?.setData('baseType', JSON.stringify(baseType));
  }

  onDragEnd() {
    this.draggedItem = null;
  }

  onDragEnter(event: DragEvent, tier: TierDataVm) {
    event.preventDefault();
    tier.dragCounter = (tier.dragCounter || 0) + 1;
    tier.isDragOver = true;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
  }

  onDragLeave(event: DragEvent, tier: TierDataVm) {
    tier.dragCounter = (tier.dragCounter || 0) - 1;
    if (tier.dragCounter === 0) {
      tier.isDragOver = false;
    }
  }

  onDrop(event: DragEvent, targetTier: TierDataVm) {
    event.preventDefault();
    targetTier.dragCounter = 0;
    targetTier.isDragOver = false;
    const baseTypeJson = event.dataTransfer!.getData('baseType');
    if (!baseTypeJson) return;
    const baseType = JSON.parse(baseTypeJson) as TieredChanceBaseVm;
    this.moveItemToTier(baseType, targetTier.tier || targetTier.name);
    this.draggedItem = null;
  }

  resetToOriginalTier(baseType: TieredChanceBaseVm) {
    this.filterService.resetChanceBaseItemTier(baseType.baseType);
    this.tieredChanceBases = this.chanceBasesTierService.getTieredChanceBases();
    this.buildTierList();
  }

  rebuildTierList() {
    this.uniquesTierService.combineTieredUniques(this.filter);
    this.chanceBasesTierService.combineTieredChanceBases(this.filter);
    this.tieredChanceBases = this.chanceBasesTierService.getTieredChanceBases();
    this.buildTierList();
    this.updateFilter();
  }

  moveItemToTier(baseType: TieredChanceBaseVm, targetTier: ChanceBaseTierType) {
    if (baseType.tier === targetTier) return;
    this.filterService.updateChanceBaseItemTier(baseType.baseType, targetTier);
    this.tieredChanceBases = this.chanceBasesTierService.getTieredChanceBases();
    this.buildTierList();
  }

  moveToWanted(baseType: TieredChanceBaseVm) {
    this.moveItemToTier(baseType, 'Searching');
  }

  moveToPotential(baseType: TieredChanceBaseVm) {
    this.moveItemToTier(baseType, 'Potential');
  }

  hideInfoMessage() {
    this.settings.dismissUniquesTierListMessage();
  }
}
